import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  CheckCircle,
  EnvelopeSimple,
  SpinnerGap,
  X,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const INITIAL_BETA = {
  name: "",
  email: "",
  company: "",
  role: "",
  use_case: "",
  privacy_acknowledged: false,
  website: "",
};


const INITIAL_SUPPORT = {
  name: "",
  email: "",
  topic: "technical",
  message: "",
  website: "",
};

export default function PublicContactDialog({
  kind,
  open,
  onClose,
  initialEmail = "",
  analyticsSource = "landing",
  analyticsSection = "contact_dialog",
  analyticsUseCase = "",
}) {
  const isBeta = kind === "beta";
  const [form, setForm] = useState(isBeta ? INITIAL_BETA : INITIAL_SUPPORT);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [successStatus, setSuccessStatus] = useState("");
  const [successEmailSent, setSuccessEmailSent] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    setForm(
      isBeta
        ? { ...INITIAL_BETA, email: initialEmail.trim().toLowerCase() }
        : INITIAL_SUPPORT
    );
    setSuccess("");
    setSuccessStatus("");
    setSuccessEmailSent(false);
    setError("");
    setSubmitting(false);
  }, [isBeta, open, initialEmail]);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const focusInitialControl = () => closeButtonRef.current?.focus();
    const focusFrame = window.requestAnimationFrame(focusInitialControl);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll(focusableSelector))
        .filter((element) => element.tabIndex >= 0 && !element.hasAttribute("disabled") && element.getAttribute("aria-hidden") !== "true");

      if (!focusable.length) {
        event.preventDefault();
        closeButtonRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialogRef.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        closeButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      document.body.style.overflow = previousOverflow;
      const previousFocus = previousFocusRef.current;
      if (previousFocus && document.contains(previousFocus)) {
        window.requestAnimationFrame(() => previousFocus.focus());
      }
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (key === "use_case") setError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");


    setSubmitting(true);
    try {
      const endpoint = isBeta ? "/public/beta-requests" : "/public/support";
      const { data } = await api.post(endpoint, form);
      const alreadyAuthorized = isBeta && data?.status === "already_authorized";
      const pendingVerification = isBeta && data?.status === "pending_verification";
      trackPublicEvent(
        pendingVerification
          ? "beta_pending_verification_recognized"
          : alreadyAuthorized
            ? "beta_existing_access_recognized"
            : isBeta
              ? "beta_request_submitted"
              : "support_request_submitted",
        {
          source: analyticsSource,
          section: analyticsSection,
          use_case: analyticsUseCase,
        }
      );
      setSuccessStatus(data?.status || "");
      setSuccessEmailSent(Boolean(data?.email_sent));
      setSuccess(data?.message || "Your request has been received.");
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Your request could not be submitted right now. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="public-contact-overlay fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/75 px-4 py-4 backdrop-blur-sm sm:items-center sm:py-8"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="public-contact-dialog relative w-full max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b10] shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="public-contact-title"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="border-b border-white/[0.07] px-6 py-5 sm:px-7">
          <div className="pr-10">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-300">
              {isBeta ? "Private beta" : "Callsect support"}
            </div>
            <h2 id="public-contact-title" className="mt-2 text-2xl font-semibold text-white">
              {isBeta
                ? successStatus === "pending_verification"
                  ? "Account verification"
                  : successStatus === "already_authorized"
                    ? "Beta access"
                    : "Request beta access"
                : "How can we help?"}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              {isBeta
                ? successStatus === "pending_verification"
                  ? "Your Callsect account already exists, but this email still needs to be verified."
                  : successStatus === "already_authorized"
                    ? "This email is already approved for the Callsect private beta."
                    : "Your request will be reviewed first. Submitting this form does not create an account or send an invitation automatically."
                : "Send the details here and Callsect will deliver them directly to support."}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }}
            className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-slate-400 transition hover:bg-white/[0.06] hover:text-white"
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="px-6 py-9 text-center sm:px-8">
            <CheckCircle size={46} weight="fill" className="mx-auto text-emerald-400" />
            <h3 className="mt-4 text-xl font-semibold text-white">
              {successStatus === "pending_verification"
                ? "Your account is waiting for verification"
                : successStatus === "already_authorized"
                  ? "You already have beta access"
                  : "Request received"}
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-slate-400">{success}</p>
            {isBeta ? (
              <div className="mx-auto mt-5 flex max-w-md items-start gap-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.07] p-4 text-left">
                <EnvelopeSimple size={20} className="mt-0.5 shrink-0 text-amber-300" />
                <p className="text-sm leading-6 text-amber-100/80">
                  {successStatus === "pending_verification" ? (
                    successEmailSent ? (
                      <>A fresh verification link is on its way. Please also check <strong className="font-semibold text-amber-100">Junk or Spam</strong> and mark Callsect as a safe sender.</>
                    ) : (
                      <>Your account is still waiting for verification. Please try again later to resend the verification email.</>
                    )
                  ) : successStatus === "already_authorized" ? (
                    successEmailSent ? (
                      <>A fresh registration link is on its way. Please also check <strong className="font-semibold text-amber-100">Junk or Spam</strong> and mark Callsect as a safe sender.</>
                    ) : (
                      <>Your beta approval is still valid. Please try again later to resend the registration email.</>
                    )
                  ) : (
                    <>If your request is approved, the invitation will arrive by email. Please also check <strong className="font-semibold text-amber-100">Junk or Spam</strong> and mark Callsect as a safe sender.</>
                  )}
                </p>
              </div>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="mt-7 inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-semibold text-black transition hover:bg-slate-200"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 px-6 py-6 sm:px-7">
            <div className="grid gap-4 sm:grid-cols-2">
              <ContactField
                label="Name"
                value={form.name}
                onChange={(value) => update("name", value)}
                autoComplete="name"
                required
              />
              <ContactField
                label="Email"
                type="email"
                value={form.email}
                onChange={(value) => update("email", value)}
                autoComplete="email"
                required
              />
            </div>

            {isBeta ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <ContactField
                    label="Company"
                    value={form.company}
                    onChange={(value) => update("company", value)}
                    autoComplete="organization"
                  />
                  <ContactField
                    label="Role"
                    value={form.role}
                    onChange={(value) => update("role", value)}
                    autoComplete="organization-title"
                  />
                </div>
                <ContactTextarea
                  label="How would you use Callsect? (optional)"
                  value={form.use_case}
                  onChange={(value) => update("use_case", value)}
                  placeholder="Briefly describe your calls, workflow, and what you want to test."
                />
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/[0.08] bg-white/[0.025] p-4 text-sm leading-6 text-slate-400">
                  <input
                    type="checkbox"
                    checked={form.privacy_acknowledged}
                    onChange={(event) => update("privacy_acknowledged", event.target.checked)}
                    className="mt-1 h-4 w-4 accent-indigo-500"
                    required
                  />
                  <span>
                    I have read the Callsect{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      className="text-indigo-300 underline decoration-indigo-400/40 underline-offset-2 hover:text-indigo-200"
                    >
                      Privacy Notice
                    </a>{" "}
                    and agree that these details may be used to review and respond to my beta-access request.
                  </span>
                </label>
              </>
            ) : (
              <>
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Topic
                  </span>
                  <select
                    value={form.topic}
                    onChange={(event) => update("topic", event.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-[#101016] px-3.5 py-3 text-sm text-white outline-none transition focus:border-indigo-400/50 focus:ring-2 focus:ring-indigo-500/15"
                  >
                    <option value="access">Beta access</option>
                    <option value="account">Account</option>
                    <option value="technical">Technical issue</option>
                    <option value="privacy">Privacy or data</option>
                    <option value="other">Other</option>
                  </select>
                </label>
                <ContactTextarea
                  label="Message"
                  value={form.message}
                  onChange={(value) => update("message", value)}
                  placeholder="Tell us what happened and what you need."
                  required
                />
              </>
            )}

            <label className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              Website
              <input
                tabIndex="-1"
                autoComplete="off"
                value={form.website}
                onChange={(event) => update("website", event.target.value)}
              />
            </label>

            {error ? (
              <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.07] px-4 py-3 text-sm leading-6 text-rose-200" role="alert">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-400/40 bg-indigo-600 px-5 py-3.5 font-medium text-white shadow-[0_0_35px_rgba(79,70,229,0.18)] transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? <SpinnerGap size={18} className="animate-spin" /> : null}
              {isBeta ? "Submit access request" : "Send to support"}
              {!submitting ? <ArrowRight size={17} weight="bold" /> : null}
            </button>
          </form>
        )}
      </section>
    </div>,
    document.body
  );
}

function ContactField({ label, type = "text", value, onChange, autoComplete, required = false }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}{required ? " *" : ""}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required={required}
        className="mt-2 w-full rounded-xl border border-white/10 bg-[#101016] px-3.5 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-indigo-400/50 focus:ring-2 focus:ring-indigo-500/15"
      />
    </label>
  );
}

function ContactTextarea({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  minLength,
  helperText,
  characterCount,
  invalid = false,
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
        {label}{required ? " *" : ""}
      </span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        minLength={minLength}
        aria-invalid={invalid || undefined}
        rows={5}
        className={`mt-2 w-full resize-y rounded-xl border bg-[#101016] px-3.5 py-3 text-sm leading-6 text-white outline-none transition placeholder:text-slate-600 focus:ring-2 ${
          invalid
            ? "border-rose-400/50 focus:border-rose-400/70 focus:ring-rose-500/15"
            : "border-white/10 focus:border-indigo-400/50 focus:ring-indigo-500/15"
        }`}
      />
      {(helperText || Number.isFinite(characterCount)) && (
        <span className="mt-1.5 flex items-center justify-between gap-3 text-xs text-slate-500">
          <span>{helperText}</span>
          {Number.isFinite(characterCount) ? (
            <span className={invalid ? "text-rose-300" : undefined}>
              {characterCount}{minLength ? ` / ${minLength}` : ""}
            </span>
          ) : null}
        </span>
      )}
    </label>
  );
}
